import { AppWorkerRole } from "../API/AppWorkerRole"
import {
    APICollaborationModel,
    CollaboratorRole
} from "../API/CollaboratorModel"
import { SystemRole } from "../API/UserModel"
import { AppPermissionScope } from "./AppPermissionScope"

export type ProjectRole = CollaboratorRole | SystemRole | AppWorkerRole

export function canEditBy(
    model: APICollaborationModel,
    email: string,
    role: ProjectRole[]
): boolean {
    return (
        model.invitedEmail !== email &&
        model.collaboratorRole !== CollaboratorRole.OWNER &&
        hasAppPermission(role, "collaborators:update")
    )
}

const ownerAndAdmin = [CollaboratorRole.OWNER, CollaboratorRole.ADMIN]

const exceptCollabMember = [
    CollaboratorRole.OWNER,
    CollaboratorRole.ADMIN,
    CollaboratorRole.INSTRUCTOR
]

const allCollaborators = [...exceptCollabMember, CollaboratorRole.MEMBER]

const allWorkers = [AppWorkerRole.REVIEWER, AppWorkerRole.LABELER]

const allCollaboratorsAndWorkers = [...allCollaborators, ...allWorkers]

/**
 * Access control list
 *
 * NOTE: SYSADMIN is omitted
 */
const permissions: { [key in AppPermissionScope]: ProjectRole[] } = {
    "app:view": allCollaboratorsAndWorkers,
    "app:rename": ownerAndAdmin,
    "app:archive": ownerAndAdmin,
    "stats:view": allCollaborators,
    "stats:refresh": [],
    "collaborators:view": allCollaborators,
    "collaborators:invite": ownerAndAdmin,
    "collaborators:update": ownerAndAdmin,
    "collaborators:remove": ownerAndAdmin,
    "crowd:view": ["SYSCROWDMANAGER"],
    "workers:view": ownerAndAdmin,
    "workers:invite": [CollaboratorRole.OWNER],
    "workers:change-role": ownerAndAdmin,
    "workers:remove": ownerAndAdmin,
    "task-design:view": allCollaborators,
    "task-design:update": ownerAndAdmin,
    "llm-params:view": [],
    "settings:view": ownerAndAdmin,
    "settings:update": ownerAndAdmin,
    "messages:view": allCollaboratorsAndWorkers,
    "messages:send": allCollaboratorsAndWorkers,
    "ai:view": [],
    "ai:add": [],
    "ai:update": [],
    "ai:remove": [],
    "ai-training:view": [],
    "ai-training:train": [],
    "bot:add": ownerAndAdmin,
    "bot:remove": ownerAndAdmin,
    "job:submit": allCollaborators,
    "task:label": allWorkers,
    "job:view": allCollaboratorsAndWorkers,
    "job:download": [...allCollaborators, AppWorkerRole.REVIEWER],
    "job:review": [AppWorkerRole.REVIEWER],
    "job:edit": [AppWorkerRole.REVIEWER],
    "job:edit-tags": allCollaborators,
    "job:relabel": allCollaborators,
    "job:cancel": allCollaborators,
    "job:delete": allCollaborators,
    "ground-truth:create": allCollaborators,
    "ground-truth:view": allCollaborators,
    "ground-truth:edit": allCollaborators,
    "ground-truth:download": allCollaborators,
    "training:create": [...exceptCollabMember, "SYSCROWDMANAGER"],
    "whosqualified:view": ["SYSCROWDMANAGER"]
}

export function hasAppPermission(
    roles: ProjectRole[],
    scope: keyof typeof permissions
): boolean {
    if (
        scope === "workers:invite" &&
        (roles.includes("SYSADMIN") || roles.includes("SYSCROWDMANAGER"))
    ) {
        // This means sysadmin cannot invite workers to their own app,
        // but sysadmin is not supposed to create apps in the first place...
        return false
    }

    return roles.some(
        role => role === "SYSADMIN" || permissions[scope].includes(role)
    )
}
